import React from "react";
import {autobind} from "core-decorators";
import {action, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";
import {Link, Redirect, RouteComponentProps} from "react-router-dom";
import {Alert, Button, message, Spin} from "antd";

import "./SignUpInvitation.scss";
import {UserInvitationQuery, UserInvitationQueryVariables} from "@/graphql/types";
import {paths} from "@/routes";
import {signUpWithInvitation} from "@/lib/backend";
import {ILocationStateReferrerProps} from "@/routes/Routes";
import logo from "@/assets/logo.svg";
import SignUpInvitationForm from "./SignUpInvitationForm";
import Store from "@/stores";

// https://www.pluralsight.com/guides/react-router-typescript
export interface ISignUpInvitationProps
  extends RouteComponentProps<{userInvitationId: string}, {}, ILocationStateReferrerProps> {
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class SignUpInvitation extends React.Component<ISignUpInvitationProps, any> {
  @observable error?: string;
  @observable userInvitation?: UserInvitationQuery["user_invitation"];

  async componentDidMount() {
    await this.loadUserInvitation();
  }

  async loadUserInvitation() {
    const {userInvitationId} = this.props.match.params;
    const result = await this.props.store!.apollo.client.query<UserInvitationQuery, UserInvitationQueryVariables>({
      query: gql`
        query UserInvitationQuery($userInvitationId: uuid!) {
          user_invitation: user_invitations_by_pk(id: $userInvitationId) {
            id
            status
            user_email
          }
        }
      `,
      variables: {userInvitationId},
    });
    const userInvitation = result.data?.user_invitation;
    if (!userInvitation) this.setError("This invitation does no longer exist");
    else if (userInvitation.status !== "pending") this.setError("This invitation has already been used");
    else this.setUserInvitation(userInvitation);
  }

  // Referrer location (it has been set in routes/Routes)
  get referrerLocation() {
    return this.props.location.state?.referrer || {pathname: paths.home()};
  }

  async onSubmitSignUpInvitationForm({name, password}: {name: string; email: string; password: string}) {
    const {history} = this.props.store!;
    const {id: invitationId} = this.userInvitation!;
    const confirmSuccessUrl = `${window.location.origin}${paths.signIn()}`;
    try {
      await signUpWithInvitation({name, password, invitationId, confirmSuccessUrl});
      history.history.push(paths.signUpSuccess());
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Oops! Something went wrong");
    }
  }

  @action
  setError(error: string) {
    this.error = error;
  }

  @action
  setUserInvitation(userInvitation: UserInvitationQuery["user_invitation"]) {
    this.userInvitation = userInvitation;
  }

  public render() {
    const store = this.props.store!;
    if (store.session.isSignedIn) return <Redirect to={this.referrerLocation} />;
    return (
      <div className="SignUpInvitation">
        <img className="Logo" src={logo} alt="Logo" />
        {this.error ? (
          <>
            <Alert message={this.error} type="error" />
            <Link to={paths.signIn()}>
              <Button type="primary" size="large" style={{marginTop: 20}}>
                Sign in
              </Button>
            </Link>
          </>
        ) : !this.userInvitation ? (
          <Spin tip="Loading invitation..." size="large" />
        ) : (
          <SignUpInvitationForm email={this.userInvitation.user_email} onSubmit={this.onSubmitSignUpInvitationForm} />
        )}
      </div>
    );
  }
}
